/**
 * SubscriptionTab.js
 * Subscription management tab for TenantDetailView.
 *
 * Flow:
 *   1. Load available plans + current tenant subscription + coupons on mount.
 *   2. If a Stripe subscription exists → show status, invoices, Send / Record payment.
 *   3. If no subscription → card (auto-charge) or cash/check (send invoice).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Icons } from '../common/Icons';
import { subscriptionsService } from '../../services/api';

// ---------------------------------------------------------------------------
// Stripe singleton
// ---------------------------------------------------------------------------
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const STATUS_BADGE = {
  active:     { label: 'Active',     color: 'var(--success, #22c55e)' },
  trialing:   { label: 'Trial',      color: 'var(--warning, #f59e0b)' },
  past_due:   { label: 'Past Due',   color: 'var(--danger,  #ef4444)' },
  canceled:   { label: 'Cancelled',  color: 'var(--text-muted)'       },
  unpaid:     { label: 'Unpaid',     color: 'var(--danger,  #ef4444)' },
  incomplete: { label: 'Incomplete', color: 'var(--warning, #f59e0b)' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { label: status, color: 'var(--text-muted)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600,
      background: cfg.color + '22', color: cfg.color, border: `1px solid ${cfg.color}44`,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function fmtCents(cents) {
  if (!cents && cents !== 0) return '—';
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function couponLabel(c) {
  const disc = c.percent_off
    ? `${c.percent_off}% off`
    : `${fmtCents(c.amount_off)} off`;
  const dur = c.duration === 'forever'
    ? 'forever'
    : c.duration === 'once'
      ? 'first payment'
      : `${c.duration_in_months} months`;
  return `${c.name}  —  ${disc} · ${dur}`;
}

const CARD_STYLE = {
  style: {
    base: {
      fontSize: '15px',
      color: '#e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      iconColor: '#94a3b8',
      '::placeholder': { color: '#64748b' },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
};

const inputStyle = {
  padding: '8px 12px',
  border: '1px solid var(--border-color, #e5e7eb)',
  borderRadius: 6,
  fontSize: 14,
  width: '100%',
  background: 'var(--bg-primary, #fff)',
  color: 'var(--text-primary)',
};

// ---------------------------------------------------------------------------
// Inline coupon creator
// ---------------------------------------------------------------------------
function CouponCreator({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: '',
    discount_type: 'percent',
    percent_off: '',
    amount_off: '',
    duration: 'forever',
    duration_in_months: '',
    redeem_by: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name:          form.name,
        discount_type: form.discount_type,
        duration:      form.duration,
      };
      if (form.discount_type === 'percent') {
        payload.percent_off = Number(form.percent_off);
      } else {
        payload.amount_off = Math.round(Number(form.amount_off) * 100); // dollars → cents
      }
      if (form.duration === 'repeating') {
        payload.duration_in_months = Number(form.duration_in_months);
      }
      if (form.redeem_by) payload.redeem_by = form.redeem_by;

      const res = await subscriptionsService.createCoupon(payload);
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create coupon');
      setSaving(false);
    }
  };

  const row = { marginBottom: 12 };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div style={{ padding: 16, border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, background: 'var(--bg-secondary, #f9fafb)', marginTop: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>Create New Coupon</div>

      <div style={row}>
        <label style={label}>Coupon Name / Label</label>
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Early Adopter 20%" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...row }}>
        <div>
          <label style={label}>Discount Type</label>
          <select style={inputStyle} value={form.discount_type} onChange={e => set('discount_type', e.target.value)}>
            <option value="percent">Percent off</option>
            <option value="fixed">Fixed amount off</option>
          </select>
        </div>
        <div>
          {form.discount_type === 'percent' ? (
            <>
              <label style={label}>Percent Off</label>
              <input style={inputStyle} type="number" min="1" max="100" value={form.percent_off} onChange={e => set('percent_off', e.target.value)} placeholder="e.g. 20" />
            </>
          ) : (
            <>
              <label style={label}>Amount Off (dollars)</label>
              <input style={inputStyle} type="number" min="0.01" step="0.01" value={form.amount_off} onChange={e => set('amount_off', e.target.value)} placeholder="e.g. 10.00" />
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...row }}>
        <div>
          <label style={label}>Duration</label>
          <select style={inputStyle} value={form.duration} onChange={e => set('duration', e.target.value)}>
            <option value="forever">Forever</option>
            <option value="once">Once (first payment only)</option>
            <option value="repeating">Repeating (N months)</option>
          </select>
        </div>
        {form.duration === 'repeating' && (
          <div>
            <label style={label}>Number of Months</label>
            <input style={inputStyle} type="number" min="1" value={form.duration_in_months} onChange={e => set('duration_in_months', e.target.value)} placeholder="e.g. 3" />
          </div>
        )}
        <div>
          <label style={label}>Expires On (optional)</label>
          <input style={inputStyle} type="date" value={form.redeem_by} onChange={e => set('redeem_by', e.target.value)} />
        </div>
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
          <Icons.AlertCircle size={14} /> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving || !form.name}>
          {saving ? <><Icons.Loader size={14} className="spin" /> Creating…</> : <><Icons.Plus size={14} /> Create Coupon</>}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
      </div>

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inner payment form (must live inside <Elements>)
// ---------------------------------------------------------------------------
function PaymentForm({ tenant, plans, coupons: initialCoupons, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [selectedPlanSlug, setSelectedPlanSlug] = useState(plans[0]?.slug || '');
  const [interval, setInterval]         = useState('monthly');
  const [billingEmail, setBillingEmail] = useState(tenant.billing_email || tenant.email || '');
  const [coupons, setCoupons]           = useState(initialCoupons);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [showCreator, setShowCreator]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState(null);

  const selectedPlan   = plans.find(p => p.slug === selectedPlanSlug);
  const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

  // Effective price after discount (display only)
  const effectivePrice = () => {
    const base = interval === 'yearly'
      ? (selectedPlan?.price_yearly || 0)
      : (selectedPlan?.price_monthly || 0);
    if (!selectedCoupon) return base;
    if (selectedCoupon.percent_off) return Math.round(base * (1 - selectedCoupon.percent_off / 100));
    if (selectedCoupon.amount_off)  return Math.max(0, base - selectedCoupon.amount_off);
    return base;
  };

  const handleCouponCreated = (newCoupon) => {
    setCoupons(prev => [newCoupon, ...prev]);
    setSelectedCouponId(newCoupon.id);
    setShowCreator(false);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !selectedPlanSlug) return;
    setSubmitting(true);
    setError(null);

    try {
      // Step 1 — create SetupIntent + Stripe Customer if needed
      const setupRes = await subscriptionsService.createSetupIntent(
        tenant.id,
        billingEmail,
        tenant.name,
      );
      const { client_secret } = setupRes.data;

      // Step 2 — confirm card setup with Stripe.js
      const cardEl = elements.getElement(CardElement);
      const { setupIntent, error: stripeErr } = await stripe.confirmCardSetup(
        client_secret,
        { payment_method: { card: cardEl, billing_details: { email: billingEmail, name: tenant.name } } }
      );

      if (stripeErr) {
        setError(stripeErr.message);
        setSubmitting(false);
        return;
      }

      // Step 3 — create the subscription
      await subscriptionsService.createSubscription({
        tenant_id:         tenant.id,
        plan_slug:         selectedPlanSlug,
        billing_interval:  interval,
        payment_method_id: setupIntent.payment_method,
        billing_email:     billingEmail,
        trial_days:        0,
        coupon_id:         selectedCouponId || undefined,
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Subscription creation failed.');
      setSubmitting(false);
    }
  };

  const sectionLabel = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'block' };

  return (
    <div>
      {/* Plan selector */}
      <div className="form-group">
        <label className="form-label">Plan</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {plans.map(plan => (
            <div
              key={plan.slug}
              onClick={() => setSelectedPlanSlug(plan.slug)}
              style={{
                padding: '12px 14px', borderRadius: 8, cursor: 'pointer', transition: 'border-color 0.15s',
                border: `2px solid ${selectedPlanSlug === plan.slug ? 'var(--accent-primary)' : 'var(--border-color, #e5e7eb)'}`,
                background: selectedPlanSlug === plan.slug ? 'var(--accent-primary)11' : 'var(--bg-secondary, #f9fafb)',
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{plan.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                {fmtCents(plan.price_monthly)}/mo{plan.price_yearly ? ` · ${fmtCents(plan.price_yearly)}/yr` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing interval */}
      <div className="form-group">
        <label className="form-label">Billing Interval</label>
        <div style={{ display: 'flex', gap: 10 }}>
          {['monthly', 'yearly'].map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setInterval(opt)}
              className={`btn ${interval === opt ? 'btn-primary' : 'btn-secondary'}`}
              style={{ textTransform: 'capitalize' }}
            >
              {opt === 'monthly'
                ? `Monthly (${fmtCents(selectedPlan?.price_monthly || 0)}/mo)`
                : `Yearly (${fmtCents(selectedPlan?.price_yearly || 0)}/yr)`}
            </button>
          ))}
        </div>
      </div>

      {/* Billing email */}
      <div className="form-group">
        <label className="form-label">Billing Email</label>
        <input
          type="email"
          className="form-control"
          value={billingEmail}
          onChange={e => setBillingEmail(e.target.value)}
          placeholder="billing@example.com"
        />
      </div>

      {/* Coupon */}
      <div className="form-group">
        <label className="form-label">Discount Coupon <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            className="form-control"
            value={selectedCouponId}
            onChange={e => setSelectedCouponId(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— No coupon —</option>
            {coupons.filter(c => c.valid).map(c => (
              <option key={c.id} value={c.id}>{couponLabel(c)}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setShowCreator(s => !s)}
            style={{ whiteSpace: 'nowrap' }}
          >
            <Icons.Plus size={14} /> New Coupon
          </button>
        </div>

        {selectedCoupon && (
          <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: 'var(--success, #22c55e)11', border: '1px solid var(--success, #22c55e)44', fontSize: 13, color: 'var(--success, #16a34a)' }}>
            <Icons.CheckCircle size={14} style={{ marginRight: 6 }} />
            Coupon applied: <strong>{couponLabel(selectedCoupon)}</strong>
            {selectedCoupon.duration !== 'forever' && ' (limited duration)'}
          </div>
        )}

        {showCreator && (
          <CouponCreator
            onCreated={handleCouponCreated}
            onCancel={() => setShowCreator(false)}
          />
        )}
      </div>

      {/* Card element */}
      <div className="form-group">
        <label className="form-label">Card Details</label>
        <div style={{
          padding: '11px 14px',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 6,
          background: 'var(--bg-primary, #fff)',
        }}>
          <CardElement options={CARD_STYLE} />
        </div>
      </div>

      {/* Price summary */}
      {selectedPlan && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 6, background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)', fontSize: 14 }}>
          <span style={{ color: 'var(--text-muted)' }}>Total: </span>
          {selectedCouponId ? (
            <>
              <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginRight: 8 }}>
                {fmtCents(interval === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly)}
              </span>
              <strong style={{ color: 'var(--success, #16a34a)' }}>
                {fmtCents(effectivePrice())}/{interval === 'yearly' ? 'yr' : 'mo'}
              </strong>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                ({selectedCoupon?.duration === 'forever' ? 'ongoing' : selectedCoupon?.duration === 'once' ? 'first payment, then full price' : `${selectedCoupon?.duration_in_months} months, then full price`})
              </span>
            </>
          ) : (
            <strong>{fmtCents(interval === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly)}/{interval === 'yearly' ? 'yr' : 'mo'}</strong>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-error mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} /> {error}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={submitting || !stripe}
      >
        {submitting ? (
          <><Icons.Loader size={16} className="spin" /> Creating subscription…</>
        ) : (
          <><Icons.CreditCard size={16} /> Subscribe — {fmtCents(effectivePrice())}/{interval === 'yearly' ? 'yr' : 'mo'}</>
        )}
      </button>

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cash / send-invoice payment form (NO Stripe Elements, NO card field)
// ---------------------------------------------------------------------------
// Mirrors PaymentForm's plan/interval/coupon selection but routes to the
// send_invoice backend flow: preview the first (prorated, discounted) invoice,
// then create the subscription with a service-period note stamped on the draft
// invoice. Stripe issues the invoice; payment is recorded out-of-band later.
function CashPaymentForm({ tenant, plans, coupons: initialCoupons, onSuccess }) {
  const [selectedPlanSlug, setSelectedPlanSlug] = useState(
    // Default to the tenant's already-assigned plan if we can map it, else first
    plans.find(p => p.slug === tenant.plan_slug)?.slug || plans[0]?.slug || ''
  );
  const [interval, setInterval]         = useState('yearly');
  const [billingEmail, setBillingEmail] = useState(tenant.billing_email || tenant.email || '');
  const [coupons, setCoupons]           = useState(initialCoupons);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [showCreator, setShowCreator]   = useState(false);
  const [anchorDate, setAnchorDate]     = useState(''); // ISO yyyy-mm-dd (renewal anchor)
  const [daysUntilDue, setDaysUntilDue] = useState(30);
  const [servicePeriod, setServicePeriod] = useState('');
  const [preview, setPreview]           = useState(null);
  const [previewing, setPreviewing]     = useState(false);
  const [creating, setCreating]         = useState(false);
  const [error, setError]               = useState(null);
  const [result, setResult]             = useState(null);

  const selectedPlan   = plans.find(p => p.slug === selectedPlanSlug);
  const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

  const buildPayload = () => ({
    tenant_id: tenant.id,
    plan_slug: selectedPlanSlug,
    billing_interval: interval,
    billing_email: billingEmail,
    coupon_id: selectedCouponId || undefined,
    billing_cycle_anchor: anchorDate || undefined,
    days_until_due: Number(daysUntilDue) || 30,
    service_period_note: servicePeriod || undefined,
  });

  const handleCouponCreated = (newCoupon) => {
    setCoupons(prev => [newCoupon, ...prev]);
    setSelectedCouponId(newCoupon.id);
    setShowCreator(false);
  };

  const handlePreview = async () => {
    setPreviewing(true); setError(null); setPreview(null);
    try {
      const res = await subscriptionsService.previewCashSubscription(buildPayload());
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true); setError(null);
    try {
      const res = await subscriptionsService.createCashSubscription(buildPayload());
      setResult(res.data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Subscription creation failed.');
    } finally {
      setCreating(false);
    }
  };

  // After a successful create, show the resulting invoice summary.
  if (result) {
    const inv = result.first_invoice;
    return (
      <div style={{ padding: 16, borderRadius: 8, background: 'var(--success, #22c55e)11', border: '1px solid var(--success, #22c55e)44' }}>
        <div style={{ fontWeight: 600, color: 'var(--success, #16a34a)', marginBottom: 8 }}>
          <Icons.CheckCircle size={16} style={{ marginRight: 6 }} />
          Cash subscription created
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          Plan: <strong>{result.plan?.name}</strong> ({result.billing_interval})<br />
          {result.service_period && <>Service period: <strong>{result.service_period}</strong><br /></>}
          {inv && (
            <>
              Invoice {inv.number || inv.id}: <strong>{fmtCents(inv.total)}</strong> ({inv.status})<br />
              {inv.hosted_invoice_url && (
                <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">View hosted invoice ↗</a>
              )}
            </>
          )}
        </div>
        {result.next_step && (
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>{result.next_step}</div>
        )}
      </div>
    );
  }

  const sectionLabel = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'block' };

  return (
    <div>
      {/* Plan selector */}
      <div className="form-group">
        <label className="form-label">Plan</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {plans.map(plan => (
            <div
              key={plan.slug}
              onClick={() => setSelectedPlanSlug(plan.slug)}
              style={{
                padding: '12px 14px', borderRadius: 8, cursor: 'pointer', transition: 'border-color 0.15s',
                border: `2px solid ${selectedPlanSlug === plan.slug ? 'var(--accent-primary)' : 'var(--border-color, #e5e7eb)'}`,
                background: selectedPlanSlug === plan.slug ? 'var(--accent-primary)11' : 'var(--bg-secondary, #f9fafb)',
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{plan.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                {fmtCents(plan.price_monthly)}/mo{plan.price_yearly ? ` \u00b7 ${fmtCents(plan.price_yearly)}/yr` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing interval */}
      <div className="form-group">
        <label className="form-label">Billing Interval</label>
        <div style={{ display: 'flex', gap: 10 }}>
          {['monthly', 'yearly'].map(opt => (
            <button key={opt} type="button" onClick={() => setInterval(opt)}
              className={`btn ${interval === opt ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'capitalize' }}>
              {opt === 'monthly'
                ? `Monthly (${fmtCents(selectedPlan?.price_monthly || 0)}/mo)`
                : `Yearly (${fmtCents(selectedPlan?.price_yearly || 0)}/yr)`}
            </button>
          ))}
        </div>
      </div>

      {/* Billing email */}
      <div className="form-group">
        <label className="form-label">Billing Email</label>
        <input type="email" className="form-control" value={billingEmail}
          onChange={e => setBillingEmail(e.target.value)} placeholder="billing@example.com" />
      </div>

      {/* Renewal anchor + due window */}
      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="form-label">Renewal Anchor Date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
          <input type="date" className="form-control" value={anchorDate}
            onChange={e => setAnchorDate(e.target.value)} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Pins all future renewals to this date. The first period is prorated to it.
          </div>
        </div>
        <div>
          <label className="form-label">Days Until Due</label>
          <input type="number" min="1" className="form-control" value={daysUntilDue}
            onChange={e => setDaysUntilDue(e.target.value)} />
        </div>
      </div>

      {/* Service period text (stamped onto the invoice) */}
      <div className="form-group">
        <label className="form-label">Service Period <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(printed on invoice)</span></label>
        <input type="text" className="form-control" value={servicePeriod}
          onChange={e => setServicePeriod(e.target.value)}
          placeholder="e.g. May 1 – December 31, 2026" />
      </div>

      {/* Coupon */}
      <div className="form-group">
        <label className="form-label">Discount Coupon <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="form-control" value={selectedCouponId} onChange={e => setSelectedCouponId(e.target.value)} style={{ flex: 1 }}>
            <option value="">— No coupon —</option>
            {coupons.filter(c => c.valid).map(c => (
              <option key={c.id} value={c.id}>{couponLabel(c)}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setShowCreator(s => !s)}
            style={{ whiteSpace: 'nowrap' }}
          >
            <Icons.Plus size={14} /> New Coupon
          </button>
        </div>
        {selectedCoupon && (
          <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: 'var(--success, #22c55e)11', border: '1px solid var(--success, #22c55e)44', fontSize: 13, color: 'var(--success, #16a34a)' }}>
            <Icons.CheckCircle size={14} style={{ marginRight: 6 }} />
            {couponLabel(selectedCoupon)}
          </div>
        )}
        {showCreator && (
          <CouponCreator
            onCreated={handleCouponCreated}
            onCancel={() => setShowCreator(false)}
          />
        )}
      </div>

      {/* Preview panel */}
      {preview && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)' }}>
          <div style={{ ...sectionLabel, marginBottom: 10 }}>First Invoice Preview</div>
          {(preview.lines || []).map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
              <span>{l.description || (l.proration ? 'Proration' : 'Subscription')}</span>
              <span>{fmtCents(l.amount)}</span>
            </div>
          ))}
          {preview.discount_total ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--success, #16a34a)', marginBottom: 4 }}>
              <span>Discount</span><span>-{fmtCents(preview.discount_total)}</span>
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 8, marginTop: 6 }}>
            <span>Amount Due Now</span><span>{fmtCents(preview.amount_due)}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{preview.note}</div>
        </div>
      )}

      {error && (
        <div className="alert alert-error mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} /> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={previewing || !selectedPlanSlug}>
          {previewing ? <><Icons.Loader size={16} className="spin" /> Previewing…</> : <><Icons.Eye size={16} /> Preview Invoice</>}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating || !preview}
          title={!preview ? 'Preview the invoice first' : undefined}>
          {creating ? <><Icons.Loader size={16} className="spin" /> Creating…</> : <><Icons.FileText size={16} /> Create &amp; Issue Invoice</>}
        </button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
        Preview is required before creating. Stripe issues the invoice; no card is charged.
      </div>

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe invoice actions (send / copy / record payment)
// ---------------------------------------------------------------------------
function InvoicePanel({ invoices, onRefresh }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  if (!invoices || invoices.length === 0) {
    return (
      <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 8, background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)', color: 'var(--text-muted)', fontSize: 14 }}>
        No Stripe invoices yet. Refresh after creating a cash subscription.
      </div>
    );
  }

  const run = async (invoiceId, fn) => {
    setBusyId(invoiceId);
    setError(null);
    try {
      await fn();
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invoice action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (inv) => {
    if (!inv.hosted_invoice_url) return;
    try {
      await navigator.clipboard.writeText(inv.hosted_invoice_url);
      setCopiedId(inv.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (_) {
      setError('Could not copy link.');
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Invoices
      </div>
      {error && (
        <div className="alert alert-error mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} /> {error}
        </div>
      )}
      {invoices.map((inv) => {
        const unpaid = inv.status === 'draft' || inv.status === 'open';
        const busy = busyId === inv.id;
        return (
          <div key={inv.id} style={{ marginBottom: 12, padding: 16, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-secondary, #f9fafb)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{inv.number || inv.id}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {inv.status} · {fmtCents(inv.amount_due != null ? inv.amount_due : inv.total)} due
                  {inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ''}
                </div>
              </div>
              <StatusBadge status={inv.status === 'paid' ? 'active' : inv.status === 'open' || inv.status === 'draft' ? 'unpaid' : inv.status} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {unpaid && (
                <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                  onClick={() => run(inv.id, () => subscriptionsService.sendCashInvoice({ invoice_id: inv.id }))}>
                  {busy ? <><Icons.Loader size={14} className="spin" /> Sending…</> : <><Icons.Send size={14} /> Send invoice</>}
                </button>
              )}
              {inv.status === 'draft' && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                  onClick={() => run(inv.id, () => subscriptionsService.finalizeCashInvoice({ invoice_id: inv.id }))}>
                  <Icons.FileText size={14} /> Finalize
                </button>
              )}
              {inv.hosted_invoice_url && (
                <>
                  <a className="btn btn-secondary btn-sm" href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                    <Icons.ExternalLink size={14} /> Open hosted invoice
                  </a>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => copyLink(inv)}>
                    <Icons.Copy size={14} /> {copiedId === inv.id ? 'Copied' : 'Copy link'}
                  </button>
                </>
              )}
              {inv.invoice_pdf && (
                <a className="btn btn-secondary btn-sm" href={inv.invoice_pdf} target="_blank" rel="noreferrer">
                  <Icons.FileText size={14} /> PDF
                </a>
              )}
              {unpaid && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                  onClick={() => run(inv.id, () => subscriptionsService.recordCashPayment({ invoice_id: inv.id, finalize_if_draft: true }))}>
                  <Icons.DollarSign size={14} /> Record payment
                </button>
              )}
            </div>
          </div>
        );
      })}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active subscription detail panel
// ---------------------------------------------------------------------------
function SubscriptionDetail({ sub, onRefresh }) {
  const discount = sub.stripe?.discount;
  const invoices = sub.stripe?.invoices || [];
  const collection = sub.stripe?.collection_method;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Plan',           value: sub.plan_name || '—' },
          { label: 'Status',         value: <StatusBadge status={sub.subscription_status} /> },
          { label: 'Interval',       value: sub.billing_interval === 'yearly' ? 'Annual' : 'Monthly' },
          { label: 'Collection',     value: collection === 'send_invoice' ? 'Invoice (cash / check)' : collection === 'charge_automatically' ? 'Card (auto-charge)' : '—' },
          { label: 'Billing Email',  value: sub.billing_email || '—' },
          { label: 'Started',        value: fmtDate(sub.subscription_started_at) },
          { label: 'Renews',         value: sub.stripe?.current_period_end ? fmtDate(sub.stripe.current_period_end) : '—' },
          { label: 'Trial Ends',     value: sub.trial_ends_at ? fmtDate(sub.trial_ends_at) : 'N/A' },
          { label: 'Stripe Sub ID',  value: sub.stripe_subscription_id
              ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{sub.stripe_subscription_id}</span>
              : '—'
          },
        ].map(({ label, value }) => (
          <div key={label} style={{ padding: '14px 16px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>

      <InvoicePanel invoices={invoices} onRefresh={onRefresh} />

      {discount && (
        <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 8, background: 'var(--success, #22c55e)11', border: '1px solid var(--success, #22c55e)44' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Active Discount</div>
          <div style={{ fontWeight: 600, color: 'var(--success, #16a34a)' }}>
            <Icons.CheckCircle size={14} style={{ marginRight: 6 }} />
            {discount.coupon_name || discount.coupon_id}
            {discount.percent_off ? ` — ${discount.percent_off}% off` : discount.amount_off ? ` — ${fmtCents(discount.amount_off)} off` : ''}
            {discount.end && ` · expires ${fmtDate(discount.end)}`}
          </div>
        </div>
      )}

      <button className="btn btn-secondary btn-sm" onClick={onRefresh}>
        <Icons.RefreshCw size={14} /> Refresh from Stripe
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main exported component
// ---------------------------------------------------------------------------
export default function SubscriptionTab({ tenant }) {
  const [plans, setPlans]     = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tender, setTender]   = useState('card'); // 'card' | 'cash'

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, subRes, couponsRes] = await Promise.all([
        subscriptionsService.getPlans(),
        subscriptionsService.getTenantSubscription(tenant.id),
        subscriptionsService.getCoupons(),
      ]);
      setPlans(plansRes.data || []);
      setSub(subRes.data || null);
      setCoupons(couponsRes.data || []);
    } catch (err) {
      setError('Failed to load subscription data.');
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const hasStripeSubscription = !!sub?.stripe_subscription_id;

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <Icons.Loader size={32} className="spin" style={{ color: 'var(--accent-primary)' }} />
        <p style={{ marginTop: 12, color: 'var(--text-muted)' }}>Loading subscription…</p>
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} /> {error}
        </div>
        <button className="btn btn-secondary mt-2" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 className="card-title" style={{ margin: 0 }}>Subscription</h3>
        {sub?.subscription_status && <StatusBadge status={sub.subscription_status} />}
      </div>

      {hasStripeSubscription ? (
        <SubscriptionDetail sub={sub} onRefresh={load} />
      ) : (
        <>
          {sub?.subscription_plan_id && !sub?.stripe_subscription_id && (
            <div className="alert alert-warning mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Icons.Info size={16} />
              Plan assigned ({sub.plan_name}) but no Stripe subscription yet. Choose a payment method to activate billing.
            </div>
          )}

          {/* Tender selector: card (auto-charge) vs cash/check (send invoice) */}
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setTender('card')}
                className={`btn ${tender === 'card' ? 'btn-primary' : 'btn-secondary'}`}>
                <Icons.CreditCard size={16} /> Card (auto-charge)
              </button>
              <button type="button" onClick={() => setTender('cash')}
                className={`btn ${tender === 'cash' ? 'btn-primary' : 'btn-secondary'}`}>
                <Icons.DollarSign size={16} /> Cash / Check (send invoice)
              </button>
            </div>
          </div>

          {tender === 'card' ? (
            <>
              <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                Select a plan and enter card details to create an auto-charged Stripe subscription for <strong>{tenant.name}</strong>.
              </p>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  tenant={{ ...tenant, billing_email: sub?.billing_email || tenant.billing_email || tenant.email }}
                  plans={plans}
                  coupons={coupons}
                  onSuccess={load}
                />
              </Elements>
            </>
          ) : (
            <>
              <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                Stripe will issue an invoice (no card charged) for <strong>{tenant.name}</strong>. Record the cash/check payment when it arrives.
              </p>
              <CashPaymentForm
                tenant={{ ...tenant, billing_email: sub?.billing_email || tenant.billing_email || tenant.email, plan_slug: sub?.plan_slug }}
                plans={plans}
                coupons={coupons}
                onSuccess={load}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
